import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import './styles.css'
import {
  calculateCrownProfile,
  calculateProfile,
  calculateResistanceWall,
  profileToVectors,
  toRadians,
  type MolarProfile,
} from './geometry'
import {
  calculateMaxResistantToc,
  hasResistance,
  RECOMMENDED_MIN_HEIGHT_MM,
} from './resistance'
import {
  getMaterialStandard,
  MATERIAL_STANDARDS,
  type MaterialId,
} from './materials'
import { MATERIAL_DIAGRAMS } from './materialDiagram'
import {
  calculateProjection,
  perspectiveDistanceForSpan,
  PROJECTION_VERTICAL_SPAN,
} from './projection'

const app = document.querySelector<HTMLElement>('#app')
if (!app) throw new Error('App root was not found')

app.innerHTML = `
  <section class="simulator-shell">
    <header class="page-header">
      <div>
        <p class="eyebrow">MOLAR PREPARATION LAB</p>
        <h1>支台歯の抵抗形態<br><span>3Dシミュレーター</span></h1>
      </div>
      <p class="lede">OCとTOCを動かして、軸壁がクラウンの脱離へどう抵抗するかを立体で確かめます。</p>
    </header>

    <div class="workspace">
      <section class="stage-card" aria-label="支台歯の3D表示">
        <div class="stage-toolbar" aria-label="視点の操作">
          <div class="segmented" role="group" aria-label="視点プリセット">
            <button type="button" data-view="front" class="is-active">正面</button>
            <button type="button" data-view="side">側面</button>
            <button type="button" data-view="top">咬合面</button>
          </div>
          <button type="button" id="section-toggle" class="icon-button" aria-pressed="false">
            <span class="section-icon" aria-hidden="true"></span>断面
          </button>
        </div>

        <div id="viewport" class="viewport"></div>

        <div class="view-hint" aria-hidden="true">
          <span class="orbit-mark">↻</span>
          ドラッグで回転 ・ ピンチ／ホイールで拡大
        </div>

        <div id="result-card" class="result-card" role="status" aria-live="polite">
          <span id="result-dot" class="result-dot"></span>
          <div>
            <strong id="result-title">側方力テストの準備完了</strong>
            <span id="result-detail">ボタンを押すと力の方向と抵抗壁を表示します</span>
          </div>
        </div>

        <div class="legend" aria-label="3Dモデルの凡例">
          <span><i class="swatch tooth"></i>支台歯</span>
          <span><i class="swatch crown"></i>クラウン</span>
          <span id="resistance-legend"><i class="swatch resistance"></i>抵抗に寄与する軸壁</span>
        </div>
      </section>

      <aside class="control-card">
        <div class="control-heading">
          <div>
            <p class="eyebrow">GEOMETRY</p>
            <h2>形態を調整</h2>
          </div>
          <span class="width-badge">想定幅 10 mm</span>
        </div>

        <div class="slider-block">
          <div class="slider-label-row">
            <label for="height-slider">支台歯の高さ <small>OC</small></label>
            <output id="height-value" for="height-slider">4.0 <small>mm</small></output>
          </div>
          <input type="range" id="height-slider" min="1" max="8" step="0.5" value="4" />
          <div class="range-labels"><span>1 mm</span><span>8 mm</span></div>
          <p id="height-note" class="validation-note"></p>
        </div>

        <div class="slider-block">
          <div class="slider-label-row">
            <label for="toc-slider">総咬合面収束角 <small>TOC</small></label>
            <output id="toc-value" for="toc-slider">17.0 <small>°</small></output>
          </div>
          <input type="range" id="toc-slider" min="2" max="40" step="0.5" value="17" />
          <div class="range-labels"><span>2°</span><span>40°</span></div>
          <p class="subvalue">片側テーパー角 <strong id="taper-value">8.5°</strong></p>
        </div>

        <div class="slider-block projection-block">
          <div class="slider-label-row">
            <label for="projection-slider">パース <small>投影方式</small></label>
            <output id="projection-value" for="projection-slider">透視投影</output>
          </div>
          <input type="range" id="projection-slider" min="0" max="100" step="1" value="70" />
          <div class="range-labels"><span>平行投影</span><span>透視投影</span></div>
        </div>

        <section class="parker-card" aria-labelledby="parker-title">
          <div class="parker-title-row">
            <span class="formula-mark" aria-hidden="true">P</span>
            <div><strong id="parker-title">Parkerの抵抗形態</strong><small>現在のOCでの判定</small></div>
          </div>
          <div class="threshold-row">
            <span>抵抗を保てるTOC</span>
            <strong id="threshold-value">21.8° 以下</strong>
          </div>
          <div class="threshold-track"><span id="threshold-progress"></span><i id="current-marker"></i></div>
          <p id="threshold-note">現在のTOCは閾値内です。</p>
        </section>

        <button type="button" id="force-test" class="primary-action">
          <span class="force-arrow" aria-hidden="true">→</span>
          <span><strong>側方力を加える</strong><small>脱離方向と抵抗壁を確認</small></span>
        </button>
      </aside>
    </div>

    <section class="material-section" aria-labelledby="material-title">
      <header class="material-header">
        <div>
          <p class="eyebrow">PREPARATION STANDARD</p>
          <h2 id="material-title">材料別・支台歯形成基準比較</h2>
        </div>
        <p>材料を選ぶと、推奨されるクリアランスと軸面切削量を3Dクラウンへ反映します。</p>
      </header>

      <div class="material-tabs" role="group" aria-label="クラウン材料">
        ${MATERIAL_STANDARDS.map(
          ({ id, label }, index) =>
            `<button type="button" data-material="${id}" aria-pressed="${index === 0}">${label}</button>`,
        ).join('')}
      </div>

      <div class="material-content">
        <div class="profile-card" aria-label="選択材料の形成断面図">
          <svg viewBox="0 0 360 220" role="img" aria-labelledby="profile-diagram-title">
            <title id="profile-diagram-title">支台歯形成体と完成クラウン外形の断面比較</title>
            <path id="material-crown-path" class="crown-outline" d="M80 158 C70 126 70 82 74 54 Q180 32 286 54 C290 82 290 126 280 158" />
            <path id="material-prep-path" class="prep-outline" d="M80 158 Q86 158 86 148 L86 70 Q180 54 274 70 L274 148 Q274 158 280 158" />
            <line class="baseline" x1="38" y1="158" x2="322" y2="158" />
            <circle id="left-margin-junction" class="margin-junction" cx="80" cy="158" r="4" />
            <circle id="right-margin-junction" class="margin-junction" cx="280" cy="158" r="4" />
            <line class="measure-line occlusal" x1="180" y1="42" x2="180" y2="68" />
            <line class="measure-line axial" x1="275" y1="104" x2="293" y2="104" />
            <g class="measure-label occlusal-label">
              <rect x="127" y="10" width="106" height="30" rx="10" />
              <text id="diagram-occlusal" x="180" y="30">咬合面: 1.0 mm</text>
            </g>
            <g class="measure-label axial-label">
              <rect x="244" y="74" width="108" height="30" rx="10" />
              <text id="diagram-axial" x="298" y="94">軸面: 0.5 mm</text>
            </g>
            <g class="diagram-legend">
              <circle cx="88" cy="196" r="5" class="prep-dot" />
              <text x="99" y="200">支台歯形成体</text>
              <circle cx="210" cy="196" r="5" class="crown-dot" />
              <text x="221" y="200">完成クラウン外形</text>
            </g>
          </svg>
          <strong id="diagram-margin">シャンファー</strong>
        </div>

        <div class="standard-card">
          <div class="standard-title-row">
            <div>
              <span id="material-label">FMC</span>
              <strong id="material-full-name">全部金属冠</strong>
            </div>
            <span class="video-source-badge">提供動画の形成基準</span>
          </div>
          <dl class="standard-values">
            <div>
              <dt>咬合面クリアランス</dt>
              <dd><span id="standard-occlusal">1.0</span> <small>mm</small></dd>
              <p>咬合力の分散と材料強度</p>
            </div>
            <div>
              <dt>軸面切削量</dt>
              <dd><span id="standard-axial">0.5</span> <small>mm</small></dd>
              <p>修復物の厚み確保</p>
            </div>
            <div>
              <dt>マージン</dt>
              <dd id="standard-margin">シャンファー</dd>
              <p>辺縁形態の目安</p>
            </div>
          </dl>
          <p id="material-note" class="material-note">金属の強度を活かし、切削量を抑えた形成です。</p>
        </div>
      </div>
    </section>

    <footer class="method-note">
      <strong>学習上の注意</strong>
      <span>大臼歯を幅10 mmの回転体として単純化した教育モデルです。臨床判断には歯面形態、補助的維持形態、材料なども考慮してください。</span>
    </footer>
  </section>
`

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing element: ${selector}`)
  return element
}

const viewport = requiredElement<HTMLElement>('#viewport')
const heightSlider = requiredElement<HTMLInputElement>('#height-slider')
const tocSlider = requiredElement<HTMLInputElement>('#toc-slider')
const heightValue = requiredElement<HTMLOutputElement>('#height-value')
const tocValue = requiredElement<HTMLOutputElement>('#toc-value')
const taperValue = requiredElement<HTMLElement>('#taper-value')
const projectionSlider = requiredElement<HTMLInputElement>('#projection-slider')
const projectionValue = requiredElement<HTMLOutputElement>('#projection-value')
const heightNote = requiredElement<HTMLElement>('#height-note')
const thresholdValue = requiredElement<HTMLElement>('#threshold-value')
const thresholdNote = requiredElement<HTMLElement>('#threshold-note')
const thresholdProgress = requiredElement<HTMLElement>('#threshold-progress')
const currentMarker = requiredElement<HTMLElement>('#current-marker')
const forceButton = requiredElement<HTMLButtonElement>('#force-test')
const sectionButton = requiredElement<HTMLButtonElement>('#section-toggle')
const resultCard = requiredElement<HTMLElement>('#result-card')
const resultTitle = requiredElement<HTMLElement>('#result-title')
const resultDetail = requiredElement<HTMLElement>('#result-detail')
const materialLabel = requiredElement<HTMLElement>('#material-label')
const materialFullName = requiredElement<HTMLElement>('#material-full-name')
const standardOcclusal = requiredElement<HTMLElement>('#standard-occlusal')
const standardAxial = requiredElement<HTMLElement>('#standard-axial')
const standardMargin = requiredElement<HTMLElement>('#standard-margin')
const materialNote = requiredElement<HTMLElement>('#material-note')
const diagramOcclusal = requiredElement<SVGTextElement>('#diagram-occlusal')
const diagramAxial = requiredElement<SVGTextElement>('#diagram-axial')
const diagramMargin = requiredElement<HTMLElement>('#diagram-margin')
const materialPrepPath = requiredElement<SVGPathElement>('#material-prep-path')
const materialCrownPath = requiredElement<SVGPathElement>('#material-crown-path')
const leftMarginJunction = requiredElement<SVGCircleElement>('#left-margin-junction')
const rightMarginJunction = requiredElement<SVGCircleElement>('#right-margin-junction')

const scene = new THREE.Scene()
scene.background = new THREE.Color(0xf3f0e9)

const perspectiveCamera = new THREE.PerspectiveCamera(31.2, 1, 0.1, 300)
const orthographicCamera = new THREE.OrthographicCamera(-5.75, 5.75, 5.75, -5.75, 0.1, 300)
let camera: THREE.PerspectiveCamera | THREE.OrthographicCamera = perspectiveCamera
camera.position.set(19, 7, 0.01)
let viewportAspect = 1

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.outputColorSpace = THREE.SRGBColorSpace
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.15
renderer.localClippingEnabled = true
renderer.domElement.setAttribute('aria-label', '回転可能な支台歯とクラウンの3Dモデル')
viewport.append(renderer.domElement)

const controls = new OrbitControls(camera, renderer.domElement)
controls.enableDamping = true
controls.dampingFactor = 0.075
controls.enablePan = false
controls.minDistance = 11
controls.maxDistance = 140
controls.target.set(0, 2.1, 0)

scene.add(new THREE.HemisphereLight(0xfffbef, 0x647076, 2.2))
const keyLight = new THREE.DirectionalLight(0xffffff, 3.4)
keyLight.position.set(6, 11, 8)
scene.add(keyLight)
const rimLight = new THREE.DirectionalLight(0xa9d7e1, 1.7)
rimLight.position.set(-8, 5, -6)
scene.add(rimLight)

const clippingPlane = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0)
const clippingPlanes: THREE.Plane[] = []

const toothMaterial = new THREE.MeshPhysicalMaterial({
  color: 0xf0e7d2,
  roughness: 0.42,
  metalness: 0,
  clearcoat: 0.18,
  side: THREE.DoubleSide,
  clippingPlanes,
})
const crownMaterial = new THREE.MeshPhysicalMaterial({
  color: 0x62b7c6,
  roughness: 0.28,
  metalness: 0.02,
  transmission: 0.08,
  transparent: true,
  opacity: 0.2,
  depthWrite: false,
  side: THREE.FrontSide,
  clippingPlanes,
})
const resistanceMaterial = new THREE.MeshPhysicalMaterial({
  color: 0xf06b47,
  emissive: 0x7c1c08,
  emissiveIntensity: 0.22,
  roughness: 0.3,
  transparent: true,
  opacity: 0.94,
  side: THREE.DoubleSide,
  clippingPlanes,
})

const modelRoot = new THREE.Group()
scene.add(modelRoot)
const toothGroup = new THREE.Group()
const crownGroup = new THREE.Group()
modelRoot.add(toothGroup, crownGroup)

const gumMaterial = new THREE.MeshStandardMaterial({ color: 0xd89887, roughness: 0.82 })
const gum = new THREE.Mesh(new THREE.CylinderGeometry(7.4, 7.8, 0.55, 96), gumMaterial)
gum.position.y = -0.38
scene.add(gum)

const rootMaterial = new THREE.MeshStandardMaterial({ color: 0xe7ddc7, roughness: 0.62 })
const root = new THREE.Mesh(new THREE.CylinderGeometry(4.75, 3.15, 2.7, 96), rootMaterial)
root.position.y = -1.6
scene.add(root)

const sectionPlane = new THREE.Mesh(
  new THREE.PlaneGeometry(12, 12),
  new THREE.MeshBasicMaterial({
    color: 0x2f6f78,
    transparent: true,
    opacity: 0.12,
    side: THREE.DoubleSide,
    depthWrite: false,
  }),
)
sectionPlane.rotation.y = Math.PI / 2
sectionPlane.position.y = 3.1
sectionPlane.visible = false
scene.add(sectionPlane)

const forceArrow = new THREE.ArrowHelper(
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-8.5, 2.5, 0),
  5.2,
  0xd84930,
  0.85,
  0.42,
)
forceArrow.visible = false
scene.add(forceArrow)

let toothMesh: THREE.Mesh | undefined
let crownMesh: THREE.Mesh | undefined
let resistanceMesh: THREE.Mesh | undefined
let isAnimating = false
let isSectionVisible = false
let selectedMaterialId: MaterialId = 'fmc'

function disposeMesh(mesh: THREE.Mesh | undefined): void {
  if (!mesh) return
  mesh.removeFromParent()
  mesh.geometry.dispose()
}

function createLathe(profile: MolarProfile): void {
  disposeMesh(toothMesh)
  disposeMesh(crownMesh)
  disposeMesh(resistanceMesh)

  toothMesh = new THREE.Mesh(
    new THREE.LatheGeometry(profileToVectors(profile.points), 128),
    toothMaterial,
  )
  toothMesh.geometry.computeVertexNormals()
  toothGroup.add(toothMesh)

  const materialStandard = getMaterialStandard(selectedMaterialId)
  crownMaterial.color.set(materialStandard.color)
  const crownProfile = calculateCrownProfile(profile, {
    radialThicknessMm: materialStandard.axialReductionMm,
    occlusalThicknessMm: materialStandard.occlusalClearanceMm,
  })
  crownMesh = new THREE.Mesh(
    new THREE.LatheGeometry(
      profileToVectors(crownProfile.points),
      128,
    ),
    crownMaterial,
  )
  crownMesh.renderOrder = 2
  crownGroup.add(crownMesh)

  resistanceMesh = new THREE.Mesh(
    new THREE.LatheGeometry(
      profileToVectors(calculateResistanceWall(profile)),
      48,
      0,
      Math.PI,
    ),
    resistanceMaterial,
  )
  resistanceMesh.visible = false
  resistanceMesh.renderOrder = 3
  toothGroup.add(resistanceMesh)

  forceArrow.position.y = profile.heightMm * 0.58
}

function updateMaterialUi(): void {
  const standard = getMaterialStandard(selectedMaterialId)
  const diagram = MATERIAL_DIAGRAMS[standard.marginType]
  materialLabel.textContent = standard.label
  materialFullName.textContent = standard.fullName
  standardOcclusal.textContent = standard.occlusalClearanceMm.toFixed(1)
  standardAxial.textContent = standard.axialReductionMm.toFixed(1)
  standardMargin.textContent = standard.margin
  materialNote.textContent = standard.note
  diagramOcclusal.textContent = `咬合面: ${standard.occlusalClearanceMm.toFixed(1)} mm`
  diagramAxial.textContent = `軸面: ${standard.axialReductionMm.toFixed(1)} mm`
  diagramMargin.textContent = standard.margin
  materialPrepPath.setAttribute('d', diagram.prepPath)
  materialCrownPath.setAttribute('d', diagram.crownPath)
  leftMarginJunction.setAttribute('cx', String(diagram.leftMargin.x))
  leftMarginJunction.setAttribute('cy', String(diagram.leftMargin.y))
  rightMarginJunction.setAttribute('cx', String(diagram.rightMargin.x))
  rightMarginJunction.setAttribute('cy', String(diagram.rightMargin.y))
  document.querySelectorAll<HTMLButtonElement>('[data-material]').forEach((button) => {
    const isSelected = button.dataset.material === selectedMaterialId
    button.classList.toggle('is-active', isSelected)
    button.setAttribute('aria-pressed', String(isSelected))
  })
}

function updateUi(): void {
  const height = Number(heightSlider.value)
  const toc = Number(tocSlider.value)
  const maxToc = calculateMaxResistantToc(height)
  const resistant = hasResistance(height, toc)

  heightValue.innerHTML = `${height.toFixed(1)} <small>mm</small>`
  tocValue.innerHTML = `${toc.toFixed(1)} <small>°</small>`
  taperValue.textContent = `${(toc / 2).toFixed(1)}°`

  const heightIsRecommended = height >= RECOMMENDED_MIN_HEIGHT_MM
  heightNote.className = `validation-note ${heightIsRecommended ? 'is-ok' : 'is-warning'}`
  heightNote.innerHTML = heightIsRecommended
    ? `<strong>推奨範囲</strong> Goodacreの推奨高さ4 mm以上です。`
    : `<strong>推奨未満</strong> 補助的維持形態も検討します。`

  thresholdValue.textContent = `${maxToc.toFixed(1)}° 以下`
  thresholdNote.textContent = resistant
    ? `現在のTOC ${toc.toFixed(1)}°は閾値内です。`
    : `現在のTOCは閾値を${(toc - maxToc).toFixed(1)}°超えています。`
  thresholdNote.className = resistant ? 'is-safe' : 'is-risk'
  thresholdProgress.style.width = `${Math.min(100, (maxToc / 40) * 100)}%`
  currentMarker.style.left = `${Math.min(100, (toc / 40) * 100)}%`

  crownGroup.rotation.z = 0
  forceArrow.visible = false
  if (resistanceMesh) resistanceMesh.visible = false
  resultCard.className = 'result-card'
  resultTitle.textContent = '側方力テストの準備完了'
  resultDetail.textContent = 'ボタンを押すと力の方向と抵抗壁を表示します'

  const standard = getMaterialStandard(selectedMaterialId)
  createLathe(
    calculateProfile(height, toc, standard.marginType, standard.axialReductionMm),
  )
}

function setSectionVisibility(visible: boolean): void {
  isSectionVisible = visible
  sectionButton.setAttribute('aria-pressed', String(visible))
  sectionButton.classList.toggle('is-active', visible)
  sectionPlane.visible = visible
  const nextPlanes = visible ? [clippingPlane] : []
  toothMaterial.clippingPlanes = nextPlanes
  crownMaterial.clippingPlanes = nextPlanes
  resistanceMaterial.clippingPlanes = nextPlanes
  toothMaterial.needsUpdate = true
  crownMaterial.needsUpdate = true
  resistanceMaterial.needsUpdate = true
}

const cameraViews: Record<string, THREE.Vector3> = {
  front: new THREE.Vector3(19, 7, 0.01),
  side: new THREE.Vector3(0.01, 7, 19),
  top: new THREE.Vector3(0.01, 21, 0.01),
}

function updateOrthographicFrustum(): void {
  const halfHeight = PROJECTION_VERTICAL_SPAN / 2
  orthographicCamera.left = -halfHeight * viewportAspect
  orthographicCamera.right = halfHeight * viewportAspect
  orthographicCamera.top = halfHeight
  orthographicCamera.bottom = -halfHeight
  orthographicCamera.updateProjectionMatrix()
}

function updateProjection(): void {
  const spec = calculateProjection(Number(projectionSlider.value))
  const target = controls.target.clone()
  const direction = camera.position.clone().sub(target).normalize()
  projectionValue.textContent = spec.label

  if (spec.kind === 'orthographic') {
    orthographicCamera.position.copy(target).addScaledVector(direction, 20)
    orthographicCamera.up.copy(camera.up)
    orthographicCamera.lookAt(target)
    orthographicCamera.zoom = 1
    updateOrthographicFrustum()
    camera = orthographicCamera
  } else {
    perspectiveCamera.fov = spec.fovDeg
    perspectiveCamera.position
      .copy(target)
      .addScaledVector(direction, perspectiveDistanceForSpan(spec.fovDeg))
    perspectiveCamera.up.copy(camera.up)
    perspectiveCamera.lookAt(target)
    perspectiveCamera.updateProjectionMatrix()
    camera = perspectiveCamera
  }

  controls.object = camera
  controls.update()
}

function setCameraView(view: string): void {
  const direction = cameraViews[view]
  if (!direction) return
  controls.target.set(0, view === 'top' ? 1.6 : 2.1, 0)
  const distance = camera instanceof THREE.PerspectiveCamera
    ? perspectiveDistanceForSpan(camera.fov)
    : 20
  camera.position.copy(controls.target).addScaledVector(direction.clone().normalize(), distance)
  camera.lookAt(controls.target)
  controls.update()
  document.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.classList.toggle('is-active', button.dataset.view === view)
  })
}

function easeOutCubic(value: number): number {
  return 1 - (1 - value) ** 3
}

function runForceTest(): void {
  if (isAnimating) return
  isAnimating = true
  forceButton.disabled = true
  heightSlider.disabled = true
  tocSlider.disabled = true

  const height = Number(heightSlider.value)
  const toc = Number(tocSlider.value)
  const resistant = hasResistance(height, toc)
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const duration = reducedMotion ? 80 : 1400
  const start = performance.now()
  forceArrow.visible = true
  if (resistanceMesh) resistanceMesh.visible = true
  resultCard.className = 'result-card is-testing'
  resultTitle.textContent = '側方力を加えています'
  resultDetail.textContent = '橙色の軸壁が力に抵抗する領域です'

  const animate = (now: number): void => {
    const progress = Math.min(1, (now - start) / duration)
    const forceProgress = Math.min(1, progress / 0.35)
    const tiltProgress = Math.max(0, (progress - 0.35) / 0.65)
    forceArrow.setLength(1.5 + forceProgress * 4.1, 0.85, 0.42)
    const tilt = resistant ? -2.4 : -13
    crownGroup.rotation.z = toRadians(tilt * easeOutCubic(tiltProgress))

    if (progress < 1) {
      requestAnimationFrame(animate)
      return
    }

    isAnimating = false
    forceButton.disabled = false
    heightSlider.disabled = false
    tocSlider.disabled = false
    resultCard.className = `result-card ${resistant ? 'is-success' : 'is-failure'}`
    resultTitle.textContent = resistant ? '抵抗力あり — 安全域' : '抵抗力不足 — 脱離リスク'
    resultDetail.textContent = resistant
      ? `TOC ${toc.toFixed(1)}° ≤ 閾値 ${calculateMaxResistantToc(height).toFixed(1)}°`
      : `TOC ${toc.toFixed(1)}° > 閾値 ${calculateMaxResistantToc(height).toFixed(1)}°`
  }

  requestAnimationFrame(animate)
}

heightSlider.addEventListener('input', updateUi)
tocSlider.addEventListener('input', updateUi)
projectionSlider.addEventListener('input', updateProjection)
forceButton.addEventListener('click', runForceTest)
sectionButton.addEventListener('click', () => setSectionVisibility(!isSectionVisible))
document.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
  button.addEventListener('click', () => setCameraView(button.dataset.view ?? 'front'))
})
document.querySelectorAll<HTMLButtonElement>('[data-material]').forEach((button) => {
  button.addEventListener('click', () => {
    selectedMaterialId = button.dataset.material as MaterialId
    updateMaterialUi()
    updateUi()
  })
})

const resizeObserver = new ResizeObserver(([entry]) => {
  const { width, height } = entry.contentRect
  if (!width || !height) return
  renderer.setSize(width, height, false)
  viewportAspect = width / height
  perspectiveCamera.aspect = viewportAspect
  perspectiveCamera.updateProjectionMatrix()
  updateOrthographicFrustum()
})
resizeObserver.observe(viewport)

function render(): void {
  controls.update()
  renderer.render(scene, camera)
  requestAnimationFrame(render)
}

updateMaterialUi()
updateUi()
updateProjection()
setCameraView('front')
render()
